import styled from "styled-components";
import { useTranslation } from "react-i18next";
import type { ChangeRequestView } from "@KassaroAB/shared/types/changeRequest";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { formatLayoutItem } from "../utils/changeRequestFormatters";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: ${spacing.x05}px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x05}px;
`;

const Cell = styled.div<{ $color: string | null }>`
  min-height: 28px;
  padding: ${spacing.x05}px ${spacing.x1}px;
  border-radius: ${borderRadius.small}px;
  border: 1px solid ${colors.border};
  background-color: ${({ $color }) => $color ?? colors.backgroundDark};
  color: ${colors.text};
  font-size: ${fontSizes.small}px;
  line-height: 1.2;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow-wrap: break-word;
  hyphens: auto;
`;

const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

const getItemColor = (item: unknown): string | null =>
  typeof item === "object" &&
  item !== null &&
  "color" in item &&
  typeof item.color === "string" &&
  HEX_COLOR_PATTERN.test(item.color)
    ? item.color
    : null;

type Props = {
  fieldKey: string;
  items: unknown[];
  changeRequestView: ChangeRequestView;
};

export function LayoutItemsPreview({
  fieldKey,
  items,
  changeRequestView,
}: Props) {
  const { t } = useTranslation();
  if (items.length === 0) {
    return <>—</>;
  }
  const cells = items.map((item, index) => (
    <Cell key={index} $color={getItemColor(item)}>
      {formatLayoutItem(changeRequestView, item, t)}
    </Cell>
  ));
  return fieldKey === "items" ? <Grid>{cells}</Grid> : <Row>{cells}</Row>;
}
